/* eslint-disable operator-linebreak */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from '@tencent/tea-component';
import { Icon } from '@tencent/met-ui';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  getVoovAuthInfo,
  XmppLoginStateEnum,
  getVoovLoginState,
  VoovLoginStateEnum,
} from '@store/authentication/authentication.slice';

import logo from '@asset/image/logo.png';
import './JoinForm.css';

// import wemeetClient from '@utils/wemeetClient';
import { getLocalStorageMediaSetting, setLocalStorageDefaultMediaSetting } from '@services/storage';
import {
  MEETING_CODE_MIN_LENGTH,
  MEETING_CODE_MAX_LENGTH,
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
} from '@constant/biz';
import { setMeetingInfoInput, getMeetingInfoInput } from '@store/meeting/meeting.slice';
import { filterDigit } from '@utils/string';
import { formatMeetingCode } from '@utils/helper/meeting';
import { checklawful, xmppLogin } from '@store/authentication/authentication.trunk';
import { GUEST_APP_ID } from '@constant/index';
import wemeetClient from '@utils/wemeetClient';
import { changeIfShowMobileVerify } from '@store/ui/ui.slice';
import { fetchAndCheckMeetingInfo } from '@store/meeting/metting.trunk';
import { isMobileBrowser } from '@utils/browser';
import { openDialog } from '@component/dialog';
import { gotoLoginPage } from '@utils/navigate';

/**
 * TODO:
 * 自动聚焦功能：
 * 页面刚进来的时候
 * 1，没到 meeting 最大长度，聚焦到 meetingCode input
 * 2. 没到 nickname 最大长度，聚焦到 nickname input
 *
 *
 * TODO: 优化项
 *
 * 查询会议信息可以在这里就做，在用户填好会议号码就查询，不需要在 entering 页面查询
 * 1. 什么时候查询
 * 2. 已经查询了，但是没收到回包就点击加入会议了
 * 3. 插叙后又换了 meetingCode
 *
 */
function JoinForm() {
  const voovAuthInfo = useAppSelector(getVoovAuthInfo);
  const meetingInfoInput = useAppSelector(getMeetingInfoInput);
  const dispatch = useAppDispatch();
  const voovLoginState = useAppSelector(getVoovLoginState);
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [meetingCodeInput, setMeetingCodeInput] = useState(
    searchParams.get('code') || meetingInfoInput.meetingCode || '',
  );

  const [loading, setLoading] = useState(false);

  const defaultMediaSetting = getLocalStorageMediaSetting();

  const [nicknameInput, setNicknameInput] = useState<string>(defaultMediaSetting.nickname || '');
  const [nicknameErrorTip, setNicknameErrorTip] = useState('');

  const [enableMedia, setEnableMedia] = useState<('enableAudio' | 'enableVideo' | '')[]>([
    defaultMediaSetting?.enableAudio ? 'enableAudio' : '',
    defaultMediaSetting?.enableVideo ? 'enableVideo' : '',
  ]);

  const NicknameInput = useRef<HTMLInputElement>(null!);

  // TODO:
  // 提取两个方法
  // 判断会议 ID 是否合法
  // 判断 nickName 是否合法
  const canJoin =
    filterDigit(meetingCodeInput).length >= MEETING_CODE_MIN_LENGTH &&
    filterDigit(meetingCodeInput).length <= MEETING_CODE_MAX_LENGTH &&
    nicknameInput.length > NICKNAME_MIN_LENGTH &&
    nicknameInput.length <= NICKNAME_MAX_LENGTH;

  useEffect(() => {
    /**
     * 从 voov 登录接口中拿到用户的 nickname 并填充到 nickname 标签上
     *
     * FIXME:
     * 1. 如果接口很慢，用户已经输入了怎么处理？
     * 2. 如果 voovAuthInfo 中间发生变化了，怎么处理？
     * 这里 nickname 的初始化应该只有由登录接口导致 voovAuthInfo 才需要更新
     */

    if (nicknameInput === '') {
      setNicknameInput(voovAuthInfo?.nickname ?? '');
    }
  }, [voovAuthInfo]);

  /**
   * TODO:
   * 1. 去掉空格，- 号, 只保留数组
   *
   * 2. onInput、onChange 的区别，哪个能被粘贴触发？
   *
   */
  const onMeetingCodeInput = (event: React.FormEvent<HTMLInputElement>) => {
    const input = filterDigit(event.currentTarget.value);

    setMeetingCodeInput(formatMeetingCode(input));
  };

  const onNicknameInput = (event: React.FormEvent<HTMLInputElement>) => {
    setNicknameInput(event.currentTarget.value);

    if (nicknameErrorTip !== '') {
      setNicknameErrorTip('');
    }
  };

  const clearNickname = () => {
    setNicknameInput('');
  };

  const onEnableMediaChange = (value: any) => {
    setEnableMedia(value);
  };

  const guestJoinFlow = async () => {
    // 企业账号发起的会议，也需要checkResult
    const mobileCheckResp = await wemeetClient.meetingCtrl.guestJoinCheck(filterDigit(meetingCodeInput));
    console.log('mobileCheckResp:', mobileCheckResp);

    const checkResult = Number(mobileCheckResp.guest_join_check?.result || '1');
    // 1:入会, 0：需要登录
    if ((checkResult & 0x01) === 0) {
      forbiddenGuestJoin();
      setLoading(false);
      return;
    }

    // 根据后台企业的白名单确定是否要游客验证手机号，目前现网只有一个企业在白名单内
    if (mobileCheckResp.guest_join_check?.forbidden_sms === '0') {
      dispatch(changeIfShowMobileVerify(true));
      setLoading(false);
      return;
    }

    const lawfulResult = await dispatch(checklawful(''));
    if (!lawfulResult.payload) {
      // 不要合规
      joinMeeting();
    }

    setLoading(false);
  };

  const forbiddenGuestJoin = () => {
    openDialog({
      title: t('当前会议不支持游客入会，请登录账号后重试。'),
      content: '',
      btnConfigs: [
        {
          text: t('暂不入会'),
        },
        {
          text: t('立即登录'),
          type: 'primary',
          callback: () => {
            gotoLoginPage(true, {
              code: filterDigit(meetingCodeInput),
            });
          },
        },
      ],
    });
    return;
  };

  /**
   * todo 函数太长了，优化
   */
  const onClickJoinMeeting = async () => {
    if (nicknameInput.trim() === '') {
      setNicknameErrorTip(t('昵称不能全为空格'));
      return;
    }

    /**
     * 用户点击加入会议按钮的时候记住媒体选项
     */
    setLocalStorageDefaultMediaSetting({
      enableAudio: enableMedia.includes('enableAudio'),
      enableVideo: enableMedia.includes('enableVideo'),
      nickname: nicknameInput,
    });

    dispatch(
      setMeetingInfoInput({
        meetingCode: filterDigit(meetingCodeInput),
        nickname: nicknameInput,
        enableAudio: enableMedia.includes('enableAudio'),
        enableVideo: enableMedia.includes('enableVideo'),
      }),
    );

    setLoading(true);

    // @ts-ignore
    function loopVoovLoginState() {
      setTimeout(() => {
        (async () => {
          if (voovLoginState === VoovLoginStateEnum.succeeded) {
            const result = await dispatch(xmppLogin());

            const meetingResult = await dispatch(fetchAndCheckMeetingInfo(filterDigit(meetingCodeInput)));

            // @ts-ignore
            const { checkResult } = meetingResult.payload;

            if (!checkResult) {
              setLoading(false);
              return;
            }
            if (result.payload === XmppLoginStateEnum.succeeded) {
              console.log('voovAuthInfo:', voovAuthInfo);
              if (voovAuthInfo?.appid === GUEST_APP_ID) {
                guestJoinFlow();
              } else {
                joinMeeting();
              }
            } else {
              setLoading(false);
            }
          } else {
            loopVoovLoginState();
          }
        })();
      }, 20);
    }

    loopVoovLoginState();
  };
  // 用户点击加入会议
  // 1. 记住媒体选项
  // 2. 用户的输入存在 store 里
  // 3. 跳转到 entering 统一建立链接/进会
  const joinMeeting = async () => {
    setLoading(false);
    navigate(`/${filterDigit(meetingCodeInput)}/${window.location.search}`);
  };

  const onMeeingCodeInputKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      if (canJoin) {
        return onClickJoinMeeting();
      }

      NicknameInput.current.focus();
    }
  };

  const onNicknameInputKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      if (canJoin) {
        return onClickJoinMeeting();
      }
    }
  };

  return (
    <div className={`join-form ${isMobileBrowser() ? 'join-form-mobile' : ''}`}>
      <img src={logo} alt="voov meeting login" className="join-form__logo" />

      <h6 className="join-form__title">{t('加入会议')}</h6>

      {/* 会议号 */}
      <div className="join-form__item join-form__code-item">
        <div className="join-form__label">{t('会议号')}</div>
        <input
          className="join-form__input"
          placeholder={t('请输入会议号')}
          value={meetingCodeInput}
          onInput={onMeetingCodeInput}
          onKeyPress={onMeeingCodeInputKeyPress}
        />
      </div>

      {/* 昵称 */}
      <div className="join-form__item join-form__nickname-item">
        {nicknameInput.length > 0 && (
          <Icon type="close" className="join-form__close" size={10} onClick={clearNickname} />
        )}

        <div className="join-form__label"> {t('您的名称')}</div>
        <input
          className="join-form__input"
          placeholder={t('请输入入会姓名')}
          value={nicknameInput}
          onInput={onNicknameInput}
          onKeyPress={onNicknameInputKeyPress}
          ref={NicknameInput}
        />
        {/* 错误提示 */}
        {nicknameErrorTip !== '' && <div className="join-form__error-tip">{nicknameErrorTip}</div>}
      </div>

      <div className="join-form__media">
        <Checkbox.Group value={enableMedia} onChange={onEnableMediaChange} layout="column">
          <Checkbox name="enableAudio">{t('开启麦克风')}</Checkbox>
          <Checkbox name="enableVideo">{t('开启摄像头')}</Checkbox>
        </Checkbox.Group>
      </div>

      <Button
        type="primary"
        onClick={onClickJoinMeeting}
        className="join-form__button"
        disabled={!canJoin}
        loading={loading}
      >
        {t('加入会议')}
      </Button>
    </div>
  );
}

export default JoinForm;
